import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useOrganizationContext } from '@/context/organization-provider';
import { Package, Truck, Shield, Clock, Phone, ArrowRight } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { organization } = useOrganizationContext();
  const [isAnimating, setIsAnimating] = useState(false);

  const handleGetStarted = () => {
    setIsAnimating(true);
    setTimeout(() => {
      navigate('/auth');
    }, 300);
  };

  const features = [
    {
      icon: Package,
      title: 'Parcel Management',
      description: 'Efficient tracking and management of all your parcel deliveries'
    },
    {
      icon: Truck,
      title: 'Real-time Tracking',
      description: 'Track your parcels in real-time from pickup to delivery'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Your parcels are safe with our secure delivery network'
    },
    {
      icon: Clock,
      title: 'Fast Delivery',
      description: 'Quick and efficient delivery solutions for your business needs'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {organization?.logo_url ? (
              <img 
                src={organization.logo_url} 
                alt={organization.display_name || organization.name}
                className="h-8 w-8 object-contain"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            ) : (
              <div className="h-8 w-8 bg-primary rounded flex items-center justify-center">
                <span className="text-sm font-bold text-primary-foreground">
                  {(organization?.display_name || organization?.name || 'S').charAt(0)}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-foreground">
                {organization?.display_name || organization?.name || 'Swift Logistics'}
              </h1>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 font-semibold bg-primary/10 text-primary border-primary/20">
                BETA
              </Badge>
            </div>
          </div>
          
          <Button 
            onClick={handleGetStarted}
            className={`transition-all duration-300 ${isAnimating ? 'scale-105' : ''}`}
          >
            <Phone className="mr-2 h-4 w-4" />
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-3xl mx-auto space-y-8">
          <h2 className="text-5xl font-bold text-foreground leading-tight">
            Streamline Your <span className="text-primary">Parcel Delivery</span> Operations
          </h2>
          
          <p className="text-xl text-muted-foreground leading-relaxed">
            Manage your parcel bookings, track deliveries, and provide exceptional service 
            with our comprehensive logistics platform designed for modern businesses.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={handleGetStarted}
              className={`transition-all duration-300 ${isAnimating ? 'scale-105' : ''}`}
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button size="lg" variant="outline">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold text-foreground mb-4">
            Why Choose Our Platform?
          </h3>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Built for efficiency, designed for growth. Our platform provides everything 
            you need to manage your parcel delivery operations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
                  <feature.icon className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary/5 py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h3 className="text-3xl font-bold text-foreground">
              Ready to Transform Your Delivery Operations?
            </h3>
            <p className="text-lg text-muted-foreground">
              Join thousands of businesses who trust our platform for their parcel management needs.
            </p>
            <Button 
              size="lg" 
              onClick={handleGetStarted}
              className={`transition-all duration-300 ${isAnimating ? 'scale-105' : ''}`}
            >
              <Phone className="mr-2 h-5 w-5" />
              Sign In with Phone
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>&copy; 2025 {organization?.display_name || organization?.name || 'Swift Logistics'}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;